import {invalidateOnSuccess, omneticApi, relatedCustomNoticesTagsOnSuccess} from '@dms/api/base';
import {UuidResponseBody, UuidResponseBodySchema} from '@dms/api/shared';

import {
  ChangeWorkshopInterestBranchApiArg,
  ChangeWorkshopInterestBranchRequestBodySchema,
  ChangeWorkshopInterestCustomerApiArg,
  ChangeWorkshopInterestCustomerRequestBodySchema,
  ChangeWorkshopInterestLeadQualityApiArg,
  ChangeWorkshopInterestLeadQualityRequestBodySchema,
  CloseWorkshopInterestApiArg,
  CreateWorkshopInterestActivityApiArg,
  CreateWorkshopInterestActivityApiResponse,
  CreateWorkshopInterestActivityRequestBodySchema,
  CreateWorkshopInterestApiArg,
  CreateWorkshopInterestRequestBodySchema,
  DeleteWorkshopInterestApiArg,
  EndWorkshopInterestApiArg,
  EndWorkshopInterestRequestBodySchema,
  GetWorkshopInterestApiArg,
  GetWorkshopInterestApiArgSchema,
  GetWorkshopInterestApiResponse,
  GetWorkshopInterestUpcomingTasksApiArg,
  GetWorkshopInterestUpcomingTasksApiResponse,
  ListWorkshopInterestActivitiesApiArg,
  ListWorkshopInterestActivitiesApiArgSchema,
  ListWorkshopInterestActivitiesApiResponse,
  OpenWorkshopInterestApiArg,
  ReopenWorkshopInterestApiArg,
  RemoveVehicleApiArg,
  SelectVehicleApiArg,
  SelectVehicleRequestBodySchema,
  SelectWorkshopInterestContactPersonApiArg,
  SelectWorkshopInterestContactPersonRequestBodySchema,
  SetWorkshopInterestSourceApiArg,
  SetWorkshopInterestSourceRequestBodySchema,
  UpdateWorkshopInterestUtmApiArg,
  UpdateWorkshopInterestUtmRequestBodySchema,
  WorkshopInterestActivityResponseBodySchema,
  WorkshopInterestResponseBodySchema,
} from './types';

const injected = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    createWorkshopInterest: build.mutation<UuidResponseBody, CreateWorkshopInterestApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest`,
        method: 'POST',
        body: queryArg.createWorkshopInterestRequestBody,
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess([
        'WorkshopInterest',
        'PersonnelOverviewWorkshopInterest',
      ]),
      extraOptions: {
        requestSchema: CreateWorkshopInterestRequestBodySchema,
        responseSchema: UuidResponseBodySchema,
      },
    }),

    getWorkshopInterest: build.query<GetWorkshopInterestApiResponse, GetWorkshopInterestApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}`,
        queryArg,
      }),
      providesTags: (result, error, queryArg) => [{type: 'WorkshopInterest', id: queryArg.id}],
      extraOptions: {
        requestSchema: GetWorkshopInterestApiArgSchema,
        responseSchema: WorkshopInterestResponseBodySchema,
      },
    }),

    changeWorkshopInterestBranch: build.mutation<void, ChangeWorkshopInterestBranchApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}/change-branch`,
        method: 'POST',
        body: queryArg.changeWorkshopInterestBranchRequestBody,
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(
        (result, queryArg: ChangeWorkshopInterestBranchApiArg) => [
          {type: 'WorkshopInterest', id: queryArg.id},
          'PersonnelOverviewWorkshopInterest',
        ]
      ),
      extraOptions: {
        requestSchema: ChangeWorkshopInterestBranchRequestBodySchema,
      },
    }),

    changeWorkshopInterestCustomer: build.mutation<void, ChangeWorkshopInterestCustomerApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}/change-customer`,
        method: 'POST',
        body: queryArg.changeWorkshopInterestCustomerRequestBody,
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'WorkshopInterest', id: queryArg.id},
        'Customer',
        ...relatedCustomNoticesTagsOnSuccess(error),
      ],
      extraOptions: {
        requestSchema: ChangeWorkshopInterestCustomerRequestBodySchema,
      },
    }),
    changeWorkshopInterestLeadQuality: build.mutation<
      void,
      ChangeWorkshopInterestLeadQualityApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}/change-lead-quality`,
        method: 'POST',
        body: queryArg.changeLeadQualityRequestBody,
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(
        (result, queryArg: ChangeWorkshopInterestLeadQualityApiArg) => [
          {type: 'WorkshopInterest', id: queryArg.id},
        ]
      ),
      extraOptions: {
        requestSchema: ChangeWorkshopInterestLeadQualityRequestBodySchema,
      },
    }),

    setWorkshopInterestSource: build.mutation<void, SetWorkshopInterestSourceApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}/source`,
        method: 'PUT',
        body: queryArg.setWorkshopInterestSourceRequestBody,
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [{type: 'WorkshopInterest', id: queryArg.id}],
      extraOptions: {
        requestSchema: SetWorkshopInterestSourceRequestBodySchema,
      },
    }),

    updateWorkshopInterestUtm: build.mutation<void, UpdateWorkshopInterestUtmApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}/utm`,
        method: 'PUT',
        body: queryArg.updateWorkshopInterestUtmRequestBody,
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [{type: 'WorkshopInterest', id: queryArg.id}],
      extraOptions: {
        requestSchema: UpdateWorkshopInterestUtmRequestBodySchema,
      },
    }),

    listWorkshopInterestActivities: build.query<
      ListWorkshopInterestActivitiesApiResponse,
      ListWorkshopInterestActivitiesApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.interestId}/activity`,
        queryArg,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'WorkshopInterest', id: queryArg.interestId},
        'WorkshopInterestActivity',
      ],
      extraOptions: {
        requestSchema: ListWorkshopInterestActivitiesApiArgSchema,
        responseSchema: WorkshopInterestActivityResponseBodySchema.array(),
      },
    }),

    createWorkshopInterestActivity: build.mutation<
      CreateWorkshopInterestActivityApiResponse,
      CreateWorkshopInterestActivityApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.interestId}/activity`,
        method: 'POST',
        body: queryArg.createWorkshopInterestActivityRequestBody,
        queryArg,
      }),
      invalidatesTags: (_, __, arg) => [{type: 'WorkshopInterest', id: arg.interestId}],
      extraOptions: {
        requestSchema: CreateWorkshopInterestActivityRequestBodySchema,
        responseSchema: UuidResponseBodySchema,
      },
    }),

    deleteWorkshopInterest: build.mutation<void, DeleteWorkshopInterestApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}`,
        method: 'DELETE',
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess([
        'WorkshopInterest',
        'PersonnelOverviewWorkshopInterest',
      ]),
    }),

    openWorkshopInterest: build.mutation<void, OpenWorkshopInterestApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}/open`,
        method: 'POST',
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: OpenWorkshopInterestApiArg) => [
        {type: 'WorkshopInterest', id: queryArg.id},
        'PersonnelOverviewWorkshopInterest',
        'WorkshopInterestActivity',
      ]),
    }),

    reopenWorkshopInterest: build.mutation<void, ReopenWorkshopInterestApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}/reopen`,
        method: 'POST',
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: ReopenWorkshopInterestApiArg) => [
        {type: 'WorkshopInterest', id: queryArg.id},
        'PersonnelOverviewWorkshopInterest',
        'WorkshopInterestActivity',
      ]),
    }),

    endWorkshopInterest: build.mutation<void, EndWorkshopInterestApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}/end`,
        method: 'POST',
        body: queryArg.endWorkshopInterestRequestBody,
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: EndWorkshopInterestApiArg) => [
        {type: 'WorkshopInterest', id: queryArg.id},
        'PersonnelOverviewWorkshopInterest',
        'WorkshopInterestActivity',
      ]),
      extraOptions: {
        requestSchema: EndWorkshopInterestRequestBodySchema,
      },
    }),

    closeWorkshopInterest: build.mutation<void, CloseWorkshopInterestApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}/close`,
        method: 'POST',
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: CloseWorkshopInterestApiArg) => [
        {type: 'WorkshopInterest', id: queryArg.id},
        'PersonnelOverviewWorkshopInterest',
        'WorkshopInterestActivity',
      ]),
    }),

    selectWorkshopInterestVehicle: build.mutation<void, SelectVehicleApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}/select-vehicle`,
        method: 'POST',
        body: queryArg.selectVehicleRequestBody,
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'WorkshopInterest', id: queryArg.id},
        ...relatedCustomNoticesTagsOnSuccess(error),
      ],
      extraOptions: {
        requestSchema: SelectVehicleRequestBodySchema,
      },
    }),

    removeWorkshopInterestVehicle: build.mutation<void, RemoveVehicleApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}/remove-vehicle`,
        method: 'POST',
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'WorkshopInterest', id: queryArg.id},
        ...relatedCustomNoticesTagsOnSuccess(error),
      ],
    }),

    selectWorkshopInterestContactPerson: build.mutation<
      void,
      SelectWorkshopInterestContactPersonApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}/contact-person`,
        method: 'PUT',
        body: queryArg.selectWorkshopInterestContactPersonRequestBody,
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [{type: 'WorkshopInterest', id: queryArg.id}],
      extraOptions: {
        requestSchema: SelectWorkshopInterestContactPersonRequestBodySchema,
      },
    }),

    getWorkshopInterestUpcomingTasks: build.query<
      GetWorkshopInterestUpcomingTasksApiResponse,
      GetWorkshopInterestUpcomingTasksApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.interestId}/upcoming-tasks`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'WorkshopInterestActivity', id: queryArg.interestId},
      ],
    }),
  }),
});

export const {
  useCreateWorkshopInterestMutation,
  useGetWorkshopInterestQuery,
  useChangeWorkshopInterestBranchMutation,
  useChangeWorkshopInterestCustomerMutation,
  useChangeWorkshopInterestLeadQualityMutation,
  useSetWorkshopInterestSourceMutation,
  useUpdateWorkshopInterestUtmMutation,
  useListWorkshopInterestActivitiesQuery,
  useCreateWorkshopInterestActivityMutation,
  useDeleteWorkshopInterestMutation,
  useOpenWorkshopInterestMutation,
  useReopenWorkshopInterestMutation,
  useEndWorkshopInterestMutation,
  useCloseWorkshopInterestMutation,
  useSelectWorkshopInterestVehicleMutation,
  useRemoveWorkshopInterestVehicleMutation,
  useSelectWorkshopInterestContactPersonMutation,
  useGetWorkshopInterestUpcomingTasksQuery,
} = injected;
